import { z } from 'zod';
import { MetadataItemZod, Modify } from './utils.js';
import { Database } from './database.types.js';
import { Account } from './account.js';
import { registry } from './registry.js';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { EmbedUser } from './index.js';

extendZodWithOpenApi(z as any);

export type DashboardSyncSettings = {
    metadata: { "all": false, "include": string[] }, // This will include the title, description, filters, metadata and thumbnail
    settings: { "all": false, "include": string[] },
    tabs: { "all": false, "include": string[] },
    datasources: { "all": false, "include": string[] },
    widgets: { "all": false, "include": string[] },
}

export const DashboardSyncSettingsZod = registry.register("DashboardSyncSettings", (z.object({
    metadata: z.object({
        all: z.boolean().openapi({ description: 'Whether all metadata are included.', example: false }),
        include: z.array(z.string()).openapi({ description: 'List of metadata fields to include.', example: ['title', 'description'] }),
    }).openapi({ description: 'Metadata sync settings.' }),
    settings: z.object({
        all: z.boolean().openapi({ description: 'Whether all settings are included.', example: false }),
        include: z.array(z.string()).openapi({ description: 'List of settings fields to include.', example: ['theme', 'timezone'] }),
    }).openapi({ description: 'Settings sync settings.' }),
    tabs: z.object({
        all: z.boolean().openapi({ description: 'Whether all tabs are included.', example: false }),
        include: z.array(z.string()).openapi({ description: 'List of tab ids to include.', example: ['tab1', 'tab2'] }),
    }).openapi({ description: 'Tabs sync settings.' }),
    datasources: z.object({
        all: z.boolean().openapi({ description: 'Whether all datasources are included.', example: false }),
        include: z.array(z.string()).openapi({ description: 'List of datasource ids to include.', example: ['ds1', 'ds2'] }),
    }).openapi({ description: 'Datasources sync settings.' }),
    widgets: z.object({
        all: z.boolean().openapi({ description: 'Whether all widgets are included.', example: false }),
        include: z.array(z.string()).openapi({ description: 'List of tab ids to include.', example: ['tab1', 'tab2'] }),
    }).openapi({ description: 'Widgets sync settings.' }),
}).openapi({ description: 'Dashboard sync settings.' })) as any);

export const DEFAULT_SYNC_SETTINGS = {
    metadata: { all: true, include: [] },
    settings: { all: true, include: [] },
    tabs: { all: true, include: [] },
    datasources: { all: true, include: [] },
    widgets: { all: true, include: [] },
}

export type DashboardSettings = {
    filters: boolean;

    theme: "dark" | "light" | "auto";
    font: string;
    dark_background: string;
    dark_foreground: string;
    dark_text: string;
    light_background: string;
    light_foreground: string;
    light_text: string;
    hide_header: boolean;
    grid_spacing: number;
    grid_height: number;
    widget_border_radius: number;
    widget_padding: number;
    custom_css: string;

    enable_tabs: boolean;
    disable_widget_interactions?: boolean;

    copilot_enabled: boolean;
    copilot_title: string;
    copilot_description: string;
    help_url?: string;
    enable_advanced_widget_creator?: boolean;
    enable_widget_insights?: boolean;

    can_ask_questions: boolean;
    can_edit_widgets: boolean;
    can_edit_widget_layout: boolean;
    can_edit_tabs: boolean;
    can_create_widgets: boolean;
    can_delete_widgets: boolean;
    can_schedule_reports?: boolean;

    disable_download_images: boolean;
    disable_download_reports: boolean;
    disable_download_documents: boolean;
    pdf_orientation?: "portrait" | "landscape";
};

export const DashboardSettingsZod = registry.register("DashboardSettings", (z.object({
    filters: z.boolean().openapi({ description: 'Whether filters are enabled for the dashboard.', example: true }),
    theme: z.enum(["dark", "light", "auto"]).openapi({ description: 'Color theme for the dashboard.', example: 'auto' }),
    font: z.string().openapi({ description: 'Font used in the dashboard.', example: 'Inter' }),
    dark_background: z.string().openapi({ description: 'Background color for dark theme.', example: '#18181b' }),
    dark_foreground: z.string().openapi({ description: 'Foreground color for dark theme.', example: '#23232a' }),
    dark_text: z.string().openapi({ description: 'Text color for dark theme.', example: '#fff' }),
    light_background: z.string().openapi({ description: 'Background color for light theme.', example: '#fff' }),
    light_foreground: z.string().openapi({ description: 'Foreground color for light theme.', example: '#f4f4f5' }),
    light_text: z.string().openapi({ description: 'Text color for light theme.', example: '#18181b' }),
    hide_header: z.boolean().openapi({ description: 'Whether to hide the dashboard header.', example: false }),
    grid_spacing: z.number().int().openapi({ description: 'Spacing between grid items.', example: 16 }),
    grid_height: z.number().int().openapi({ description: 'Height of the dashboard grid.', example: 8 }),
    border_radius: z.number().openapi({ description: 'Border radius for dashboard elements.', example: 8 }),
    custom_css: z.string().openapi({ description: 'Custom CSS for the dashboard.', example: '' }),
    copilot_title: z.string().openapi({ description: 'Title for the dashboard copilot.', example: 'AI Copilot' }),
    copilot_description: z.string().openapi({ description: 'Description for the dashboard copilot.', example: 'Ask questions about your data.' }),
    help_url: z.string().url().optional().openapi({ description: 'URL for dashboard help.', example: 'https://docs.onvo.ai' }),
    enable_advanced_widget_creator: z.boolean().optional().openapi({ description: 'Enables advanced widget creator.', example: true }),
    enable_widget_insights: z.boolean().optional().openapi({ description: 'Enables widget insights.', example: false }),
    can_ask_questions: z.boolean().openapi({ description: 'Whether users can ask questions.', example: true }),
    can_edit_widgets: z.boolean().openapi({ description: 'Whether users can edit widgets.', example: true }),
    can_edit_widget_layout: z.boolean().openapi({ description: 'Whether users can edit widget layout.', example: true }),
    can_create_widgets: z.boolean().openapi({ description: 'Whether users can create widgets.', example: true }),
    can_delete_widgets: z.boolean().openapi({ description: 'Whether users can delete widgets.', example: false }),
    can_schedule_reports: z.boolean().optional().openapi({ description: 'Whether users can schedule reports.', example: false }),
    disable_download_images: z.boolean().openapi({ description: 'Disable image downloads.', example: false }),
    disable_download_reports: z.boolean().openapi({ description: 'Disable report downloads.', example: false }),
    disable_download_documents: z.boolean().openapi({ description: 'Disable document downloads.', example: false }),
    pdf_orientation: z.enum(["portrait", "landscape"]).optional().openapi({ description: 'PDF orientation for downloads.', example: 'portrait' }),
}).openapi({ description: "Settings for a dashboard." })) as any);

export type DashboardFilter = {
    label: string;
    type: "text";
    options: string[];
    values: string[];
} | {
    label: string;
    type: "number";
    options: [number, number];
    values: [number, number];
} | {
    label: string;
    type: "date";
    options: [string, string];
    values: [string, string];
};
export const DashboardFilterZod = registry.register("DashboardFilter", (z.discriminatedUnion("type", [
    z.object({
        label: z.string().openapi({ description: 'Filter label.', example: 'Status' }),
        type: z.literal("text").openapi({ description: 'Text filter type.' }),
        options: z.array(z.string()).openapi({ description: 'Available string options.', example: ['Active', 'Inactive'] }),
        values: z.array(z.string()).openapi({ description: 'Selected string values.', example: ['Active'] }),
    }),
    z.object({
        label: z.string().openapi({ description: 'Filter label.', example: 'Range' }),
        type: z.literal("number").openapi({ description: 'Number filter type.' }),
        options: z.tuple([z.number(), z.number()]).openapi({ description: 'Available number range.', example: [0, 100] }),
        values: z.tuple([z.number(), z.number()]).openapi({ description: 'Selected number range.', example: [10, 90] }),
    }),
    z.object({
        label: z.string().openapi({ description: 'Filter label.', example: 'Date Range' }),
        type: z.literal("date").openapi({ description: 'Date filter type.' }),
        options: z.tuple([z.string(), z.string()]).openapi({ description: 'Available date range.', example: ['2023-01-01', '2023-12-31'] }),
        values: z.tuple([z.string(), z.string()]).openapi({ description: 'Selected date range.', example: ['2023-02-01', '2023-02-28'] }),
    }),
]).openapi({ description: "A filter for a dashboard." })) as any);

export type DashboardTab = {
    id: number;
    label: string;
    hidden?: boolean;
}
export const DashboardTabZod = registry.register("DashboardTab", (z.object({
    id: z.number().int().openapi({ description: 'Tab ID.', example: 1 }),
    label: z.string().openapi({ description: 'Tab label.', example: 'Overview' }),
    hidden: z.boolean().optional().openapi({ description: 'Whether the tab is hidden.', example: false }),
}).openapi({ description: "A tab in a dashboard." })) as any);

export interface DashboardMeta {
    created_by: Account;
    last_updated_by?: Account;
    widgets: number;
    datasources: number;
    embed_user: EmbedUser;
}
export const DashboardMetaZod = registry.register("DashboardMeta", (z.object({
    created_by: z.string().openapi({ description: 'ID of the account that created the dashboard.', example: 'user_abc123' }),
    last_updated_by: z.string().optional().openapi({ description: 'ID of the account that last updated the dashboard.', example: 'user_xyz789' }),
    widgets: z.number().openapi({ description: 'Number of widgets in the dashboard.', example: 5 }),
    datasources: z.number().openapi({ description: 'Number of datasources in the dashboard.', example: 2 }),
}).openapi({ description: "Metadata associated with a dashboard." })) as any);

export type Dashboard = Modify<
    Database["public"]["Tables"]["dashboards"]["Row"],
    {
        settings?: DashboardSettings;
        filters: DashboardFilter[];
        tabs: DashboardTab[];
        metadata?: { label: string; value: string; secure?: boolean; }[]
        root_dashboard?: string | null;
        parent_dashboard?: string | null;
    }
>;
export const DashboardZod = registry.register("Dashboard", (z.object({
    id: z.string().uuid().openapi({ description: 'Unique dashboard ID.', example: '9b7deda3-7e2d-458e-84e9-632e47fbe682' }),
    created_at: z.string().openapi({ description: 'Creation timestamp.', example: '2024-01-17T10:14:12.62603+00:00' }),
    created_by: z.string().uuid().nullable().openapi({ description: 'ID of the account that created the dashboard.', example: '92c680a3-a9c4-4c02-bb2d-a5d049789662' }),
    description: z.string().nullable().openapi({ description: 'Dashboard description.', example: 'A dashboard for sales analytics.' }),
    filters: z.any().openapi({ description: 'Dashboard filters (arbitrary object).' }),
    last_updated_at: z.string().openapi({ description: 'Last update timestamp.', example: '2024-02-01T08:00:00.000Z' }),
    last_updated_by: z.string().uuid().nullable().openapi({ description: 'ID of the account that last updated the dashboard.', example: '92c680a3-a9c4-4c02-bb2d-a5d049789662' }),
    metadata: z.any().openapi({ description: 'Dashboard metadata (arbitrary object).' }),
    parent_dashboard: z.string().uuid().nullable().optional().openapi({ description: 'Parent dashboard ID, if this dashboard is nested.', example: null }),
    root_dashboard: z.string().uuid().nullable().optional().openapi({ description: 'Root dashboard ID, if this dashboard is nested.', example: null }),
    settings: z.any().openapi({ description: 'Dashboard settings (arbitrary object).' }),
    tabs: z.any().openapi({ description: 'Dashboard tabs (arbitrary object).' }),
    team: z.string().uuid().openapi({ description: 'Team ID associated with the dashboard.', example: '92c680a3-a9c4-4c02-bb2d-a5d049789662' }),
    thumbnail: z.string().nullable().openapi({ description: "URL to the dashboard's thumbnail image.", example: "https://example.com/thumbnail.png" }),
    title: z.string().openapi({ description: 'Dashboard title.', example: 'Sales Dashboard' }),
    sync_settings: z.any().openapi({ description: 'Dashboard sync settings (arbitrary object).' }),
}).openapi({ description: "A dashboard that displays data in a visual format." })) as any);
